import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faBellSlash, faTimes, faHistory } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { parseISO } from "date-fns";
import api from "../lib/api.js";
import { MAP_CONFIG, MARKER_COLORS, STATUS_CONFIG } from "../lib/mapStyles.js";

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
if (TOKEN) mapboxgl.accessToken = TOKEN;

export default function MapPage() {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef({});
  const fittedRef  = useRef(false);
  const [selected, setSelected] = useState(null);
  const [popupPos, setPopupPos] = useState(null); // {x, y} pixel coords
  const qc = useQueryClient();

  const { data, refetch } = useQuery({
    queryKey: ["map-devices"],
    queryFn: () => api.get("map/devices").json(),
    refetchInterval: 15_000,
  });

  const toggleNotification = useMutation({
    mutationFn: (deviceId) =>
      api.patch(`devices/${deviceId}/toggle_notifications`).json(),
    onSuccess: () => {
      refetch();
      qc.invalidateQueries(["map-devices"]);
    },
  });

  if (!TOKEN) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div style={{ color: "var(--color-cyan)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em" }}>
          MAP OFFLINE
        </div>
        <p className="data text-sm text-center" style={{ color: "var(--color-muted)", maxWidth: 400 }}>
          Set <code style={{ color: "var(--color-cyan)" }}>VITE_MAPBOX_TOKEN</code> in{" "}
          <code style={{ color: "var(--color-cyan)" }}>frontend/.env.local</code> to enable the map.
        </p>
      </div>
    );
  }

  // Initialize map
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      ...MAP_CONFIG,
      center: [8.54, 47.37],  // Zürich
      zoom: 12,
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current = map;

    // Re-project popup position during pan/zoom
    const updatePopup = () => {
      setSelected((prev) => {
        if (!prev) return prev;
        const pt = map.project([prev.longitude, prev.latitude]);
        setPopupPos({ x: pt.x, y: pt.y });
        return prev;
      });
    };
    map.on("move", updatePopup);

    return () => map.remove();
  }, []);

  // Update markers when data changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data?.devices) return;

    const renderMarkers = () => {
      const isMobile = window.innerWidth < 768 || navigator.maxTouchPoints > 0;
    data.devices.forEach((device) => {
        const el = document.createElement("div");

        // Glow intensity and color scale with SPL between 50–100 dB
        const spl = device.spl ?? device.last_reading_spl ?? 0;
        const t = Math.max(0, Math.min(1, (+spl - 50) / 50));
        const splColor = `rgb(${Math.round(68 + 187 * t)},${Math.round(136 - 85 * t)},${Math.round(255 - 102 * t)})`;
        const color = device.status === "normal" ? splColor : (MARKER_COLORS[device.status] || MARKER_COLORS.normal);
        const isInactive = device.status === "inactive";
        const baseGlow  = isInactive ? "none" : `0 0 ${Math.round(8 + t * 24)}px ${splColor}88, 0 0 ${Math.round(20 + t * 40)}px ${splColor}44`;
        const hoverGlow = isInactive ? "none" : `0 0 ${Math.round(8 + t * 24)}px ${splColor}cc, 0 0 ${Math.round(20 + t * 40)}px ${splColor}77`;

        el.style.cssText = `
          width: 31px;
          height: 31px;
          border-radius: 50%;
          background: ${color};
          border: 2px solid ${color};
          box-shadow: ${baseGlow};
          cursor: pointer;
          transition: box-shadow 0.15s;
          ${device.rave ? `animation: pulse-red 1.2s ease-in-out infinite;` : ""}
        `;
        el.addEventListener("mouseenter", () => { el.style.boxShadow = hoverGlow; });
        el.addEventListener("mouseleave", () => { el.style.boxShadow = baseGlow; });
        el.addEventListener("click", () => {
          setSelected(device);
          const pt = map.project([device.longitude, device.latitude]);
          setPopupPos({ x: pt.x, y: pt.y });
          map.flyTo({ center: [device.longitude, device.latitude], zoom: Math.max(map.getZoom(), 14), duration: 600 });
        });

        if (markersRef.current[device.id]) {
          markersRef.current[device.id].remove();
        }

        markersRef.current[device.id] = new mapboxgl.Marker({ element: el })
          .setLngLat([device.longitude, device.latitude])
          .addTo(map);
      });

      // Fit to device bounds only on first load
      if (!fittedRef.current && data.devices.length > 0) {
        fittedRef.current = true;
        const bounds = new mapboxgl.LngLatBounds();
        data.devices.forEach((d) => bounds.extend([d.longitude, d.latitude]));
        map.fitBounds(bounds, { padding: 80, maxZoom: 14, duration: 800 });
      }
    };

    if (map.isStyleLoaded()) {
      renderMarkers();
    } else {
      map.once("load", renderMarkers);
    }
  }, [data]);

  // Keep popup anchored to marker while map moves/flies
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selected) return;
    const update = () => {
      const pt = map.project([selected.longitude, selected.latitude]);
      setPopupPos({ x: pt.x, y: pt.y });
    };
    map.on("move", update);
    return () => map.off("move", update);
  }, [selected]);

  // Update selected device from latest data
  const selectedDevice = selected
    ? data?.devices?.find((d) => d.id === selected.id) || selected
    : null;

  return (
    <div style={{ height: "100%", position: "relative" }}>
      <div ref={mapContainerRef} style={{ height: "100%" }} />

      {/* Legend */}
      <div
        style={{
          position: "absolute", bottom: 32, left: 16,
          background: "rgba(10,10,15,0.92)",
          border: "1px solid var(--color-border)",
          borderRadius: 8, padding: "12px 16px",
          fontFamily: "var(--font-mono)", fontSize: 11,
          backdropFilter: "blur(8px)",
        }}
      >
        {Object.entries(MARKER_COLORS).map(([status, color]) => (
          <div key={status} className="flex items-center gap-2 mb-1 last:mb-0">
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}88` }} />
            <span style={{ color: "var(--color-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {status.replace(/_/g, " ")}
            </span>
          </div>
        ))}
      </div>

      {/* Device popup panel — anchored above the marker */}
      {selectedDevice && popupPos && (
        <div
          style={{
            position: "absolute",
            left: popupPos.x,
            top: popupPos.y,
            transform: "translate(-50%, calc(-100% - 22px))",
            width: 320,
            background: "rgba(8,8,14,0.97)",
            border: `1px solid ${selectedDevice.rave ? "rgba(255,51,153,0.6)" : "rgba(68,136,255,0.5)"}`,
            borderRadius: 10,
            padding: 14,
            fontFamily: "var(--font-mono)",
            backdropFilter: "blur(16px)",
            boxShadow: selectedDevice.rave
              ? "0 0 24px rgba(255,51,153,0.25), 0 8px 32px rgba(0,0,0,0.6)"
              : "0 0 24px rgba(68,136,255,0.15), 0 8px 32px rgba(0,0,0,0.6)",
            zIndex: 10,
            pointerEvents: "auto",
          }}
        >
          {/* Arrow pointing down to marker */}
          <div style={{
            position: "absolute", bottom: -8, left: "50%", transform: "translateX(-50%)",
            width: 0, height: 0,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: `8px solid ${selectedDevice.rave ? "rgba(255,51,153,0.6)" : "rgba(68,136,255,0.5)"}`,
          }} />
          {/* Close */}
          <button
            onClick={() => { setSelected(null); setPopupPos(null); }}
            style={{
              position: "absolute", top: 10, right: 10,
              background: "none", border: "none",
              color: "var(--color-muted)", cursor: "pointer", fontSize: 14,
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {/* Device name */}
          <div
            style={{
              fontSize: 14, fontWeight: 700,
              color: selectedDevice.rave ? "var(--color-pink)" : "var(--color-blue)",
              marginBottom: 4,
              textShadow: selectedDevice.rave ? "var(--glow-pink)" : "var(--glow-blue)",
            }}
          >
            {selectedDevice.name}
          </div>

          {selectedDevice.location && (
            <div style={{ fontSize: 11, color: "var(--color-muted)", marginBottom: 10 }}>
              {selectedDevice.location}
            </div>
          )}

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
            <Stat
              label="Confidence"
              value={selectedDevice.rave_confidence != null ? `${selectedDevice.rave_confidence}%` : "—"}
              color={selectedDevice.rave_confidence != null ? (selectedDevice.rave_confidence >= selectedDevice.detection_threshold * 100 ? "var(--color-pink)" : "var(--color-cyan)") : undefined}
            />
            <Stat
              label="Threshold"
              value={selectedDevice.detection_threshold != null ? `${(selectedDevice.detection_threshold * 100).toFixed(0)}%` : "—"}
              color="var(--color-pink)"
            />
            {(() => { const sc = STATUS_CONFIG[selectedDevice.status] || STATUS_CONFIG.normal; return (
            <Stat label="Status" value={sc.label} color={sc.color} />
            ); })()}
          </div>

          {/* Confidence sparkline (24h) */}
          <PopupConfidenceSparkline deviceId={selectedDevice.id} threshold={selectedDevice.detection_threshold} />

          {selectedDevice.rave && (
            <div
              style={{
                background: "rgba(255,51,153,0.1)",
                border: "1px solid var(--color-pink)",
                borderRadius: 6, padding: "6px 10px",
                fontSize: 11, color: "var(--color-pink)",
                marginBottom: 12, letterSpacing: "0.1em",
              }}
            >
              ⚠ RAVE DETECTED
            </div>
          )}

          {/* Notification toggle */}
          <button
            onClick={() => toggleNotification.mutate(selectedDevice.id)}
            disabled={toggleNotification.isPending}
            style={{
              width: "100%",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "8px 12px", borderRadius: 6,
              background: selectedDevice.notifications_enabled
                ? "rgba(68,136,255,0.15)"
                : "rgba(122,122,154,0.1)",
              border: `1px solid ${selectedDevice.notifications_enabled ? "var(--color-blue)" : "var(--color-border)"}`,
              color: selectedDevice.notifications_enabled ? "var(--color-blue)" : "var(--color-muted)",
              cursor: "pointer", fontSize: 12, letterSpacing: "0.08em",
              transition: "all 0.2s",
            }}
          >
            <FontAwesomeIcon icon={selectedDevice.notifications_enabled ? faBell : faBellSlash} />
            {selectedDevice.notifications_enabled ? "Notifications ON" : "Notifications OFF"}
          </button>

          {/* Link to history */}
          <Link
            to={`/devices/${selectedDevice.id}`}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "8px 12px", borderRadius: 6, marginTop: 8,
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-muted)",
              fontSize: 12, letterSpacing: "0.08em",
              textDecoration: "none",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-blue)"; e.currentTarget.style.borderColor = "var(--color-blue)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-muted)"; e.currentTarget.style.borderColor = "var(--color-border)"; }}
          >
            <FontAwesomeIcon icon={faHistory} />
            View History
          </Link>
        </div>
      )}
    </div>
  );
}

function PopupConfidenceSparkline({ deviceId, threshold }) {
  const { data, isLoading } = useQuery({
    queryKey: ["confidence-history", deviceId, 24],
    queryFn: () => api.get(`devices/${deviceId}/confidence_history`, { searchParams: { hours: 24 } }).json(),
    staleTime: 60_000,
  });

  if (isLoading) return <div style={{ fontSize: 10, color: "var(--color-muted)", marginBottom: 10 }}>Loading…</div>;
  if (!data?.history?.length) return null;

  const series = data.history.map((p) => [parseISO(p.timestamp).getTime(), p.confidence]);
  const detectThreshold = data.detect_threshold ?? threshold ?? 0.65;

  const options = {
    chart: {
      backgroundColor: "transparent",
      type: "areaspline",
      height: 120,
      marginLeft: 48,
      marginRight: 8,
      style: { fontFamily: "var(--font-mono)" },
    },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: "rgba(10,10,15,0.95)",
      borderColor: "var(--color-border)",
      style: { color: "var(--color-white)", fontFamily: "var(--font-mono)", fontSize: "10px" },
      xDateFormat: "%H:%M",
      pointFormat: "Confidence: <b>{point.y:.2f}</b>",
    },
    xAxis: {
      type: "datetime",
      min: Date.now() - 24 * 60 * 60 * 1000,
      max: Date.now(),
      lineColor: "var(--color-border)",
      tickColor: "var(--color-border)",
      dateTimeLabelFormats: { hour: "%H:%M", day: "%d.%m" },
      labels: { style: { color: "var(--color-muted)", fontSize: "9px" } },
    },
    yAxis: {
      visible: true,
      title: { text: null },
      gridLineColor: "rgba(255,255,255,0.06)",
      labels: { style: { color: "var(--color-muted)", fontSize: "9px" }, formatter: function () { return Math.round(this.value * 100) + "%"; } },
      min: 0,
      max: 1,
      tickInterval: 0.25,
      plotLines: [{
        value: detectThreshold,
        color: "var(--color-pink)",
        width: 1.5,
        dashStyle: "Dash",
        zIndex: 5,
        label: {
          text: `${(detectThreshold * 100).toFixed(0)}%`,
          align: "left",
          style: { color: "var(--color-pink)", fontSize: "8px", fontFamily: "var(--font-mono)" },
          x: 0, y: -2,
        },
      }],
    },
    series: [{
      data: series,
      color: "#4488ff",
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, "rgba(68,136,255,0.2)"],
          [1, "rgba(68,136,255,0.01)"],
        ],
      },
      lineWidth: 1.2,
      marker: { enabled: false },
      zones: [
        { value: detectThreshold, color: "#4488ff" },
        { color: "#ff3399" },
      ],
    }],
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 9, color: "var(--color-muted)", letterSpacing: "0.1em", marginBottom: 2, textTransform: "uppercase" }}>
        Confidence 24h
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--color-muted)", letterSpacing: "0.1em", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: color || "var(--color-white)", fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}
